import { SwitchRow } from '@components/SwitchRow/SwitchRow';
import { ButtonBase } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import css from './CustomWordHintsToggle.module.scss';
import { EditWordsModal } from './EditWordsModal';

// Matches the Switch slider transition (0.4s in Switch.module.scss).
const SWITCH_ANIMATION_MS = 400;
const PREVIEWED_WORDS = 3;

type CustomWordHintsToggleProps = {
  checked: boolean;
  onChange: (checked: boolean) => void;
  /** The word list (owned by the parent). "Edit words" edits this via onWordsChange. */
  words: string[];
  onWordsChange: (words: string[]) => void;
  /** Passed through to EditWordsModal — see there. Used by the edit/rename flow. */
  showAppliesToNewMediaWarning?: boolean;
  /** Locks the switch + "Edit words" button (e.g. while the parent modal is saving). */
  disabled?: boolean;
};

function summarizeWords(words: string[]) {
  if (words.length === 0) return 'No words added yet';
  const shown = words.slice(0, PREVIEWED_WORDS).join(', ');
  const rest = words.length - PREVIEWED_WORDS;
  return rest > 0 ? `${shown} +${rest} more` : shown;
}

/**
 * "Custom word hints" switch, shared by the create-library and edit-library flows. When on, the
 * library keeps a short list of custom words (names, brands, jargon) that biases transcription
 * toward them, and a line under the switch shows those words with an "Edit words" button. The
 * word list is owned by the parent (via words / onWordsChange).
 */
export function CustomWordHintsToggle({
  checked,
  onChange,
  words,
  onWordsChange,
  showAppliesToNewMediaWarning,
  disabled,
}: CustomWordHintsToggleProps) {
  const [editOpen, setEditOpen] = useState(false);
  const openTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Clear the pending auto-open timer on unmount (e.g. the create/rename modal closes).
  useEffect(
    () => () => {
      if (openTimer.current) clearTimeout(openTimer.current);
    },
    [],
  );

  const handleSwitchChange = (next: boolean) => {
    onChange(next);
    if (openTimer.current) clearTimeout(openTimer.current);
    if (next) {
      // Turning the feature on jumps straight into editing words, after the switch's
      // slide animation so the two motions don't overlap.
      openTimer.current = setTimeout(() => setEditOpen(true), SWITCH_ANIMATION_MS);
    } else {
      // Turning it off closes the editor — otherwise words get edited into a list
      // that's discarded while the feature is off.
      setEditOpen(false);
    }
  };

  return (
    <div className={css.root}>
      <SwitchRow
        label="Custom word hints"
        hint="Help transcription spell names, brands and jargon correctly."
        checked={checked}
        onChange={handleSwitchChange}
        disabled={disabled}
      />
      {checked && (
        <div className={css.words}>
          <span className={css.summary} title={words.join(', ')}>
            {summarizeWords(words)}
          </span>
          <ButtonBase
            className={css.editWordsButton}
            onClick={() => setEditOpen(true)}
            disabled={disabled}
          >
            Edit words
          </ButtonBase>
        </div>
      )}
      <EditWordsModal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        words={words}
        onChange={onWordsChange}
        showAppliesToNewMediaWarning={showAppliesToNewMediaWarning}
      />
    </div>
  );
}
